// ---------------------------------------------------------------------------
// Building links that keep the current org.
//
// A support/partner admin browses another tenant by carrying `?orgId=` in the URL -- every
// store reads it (FiltersStore.orgId, OrganizationStore) and every API call sends it. A link
// that drops it silently returns the admin to their OWN org, which looks like missing data
// rather than a lost parameter. The nav bar avoids this by appending the whole search
// (Layout.tsx), and list->builder navigations do the same with `navigate({ pathname, search })`;
// these two helpers cover the cases that cannot just concatenate.
// ---------------------------------------------------------------------------

/**
 * Merge the current URL's params into a destination that carries its own.
 *
 * For links WITHIN a section, where the whole filter context should travel and the destination's
 * own params (a prefill, an action) must survive: the destination wins on a collision.
 */
export function mergeSearch(currentSearch: string, destinationSearch = ""): string {
  if (!currentSearch) {
    return destinationSearch;
  }
  const merged = new URLSearchParams(currentSearch);
  new URLSearchParams(destinationSearch).forEach((value, key) => merged.set(key, value));
  const search = merged.toString();
  return search ? `?${search}` : "";
}

/**
 * Just the org context, as a ready-to-append search string.
 *
 * For links that leave the section (a user record, say). Carrying the ENTIRE search there would
 * push page-local params into a page that reads some of the same names — `search` means something
 * on the users page — so only the org travels.
 */
export function orgIdOnly(currentSearch: string): string {
  const orgId = new URLSearchParams(currentSearch).get("orgId");
  return orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";
}
